'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import { SettingsCard, SettingsCardHeader } from '@/components/dialog/settings/settings-card'
import StepUpVerificationFields from '@/components/dialog/settings/step-up-verification-fields'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/features/auth/hooks'
import { getStepUpMode, pickStepUpVerification } from '@/lib/step-up'
import { toast } from '@/lib/toast'
import { useChangePassword } from '@/service/password.hook'
import { SensitiveAction } from '@/types/generated/common/auth'

type ChangePasswordFormData = {
  currentPassword: string
  newPassword: string
  confirmNewPassword: string
  totpCode?: string
  backupCode?: string
  emailCode?: string
}

export default function ChangePasswordCard({ is2faEnabled }: { is2faEnabled: boolean }) {
  const t = useTranslations('settings')
  // This card only renders for accounts that have a password (gated in
  // security.tsx), so current-password is always required here.
  const { hasPassword } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  // Min length 8 = the forgot-password reset form's rule
  // (confirmPasswordResetSchema in reset-password.tsx) and the backend gate.
  const schema = useMemo(
    () =>
      z
        .object({
          currentPassword: z.string().min(1, { message: t('currentPasswordRequired') }),
          newPassword: z.string().min(8, { message: t('changePassword.tooShort') }),
          confirmNewPassword: z.string(),
          totpCode: z.string().optional(),
          backupCode: z.string().optional(),
          emailCode: z.string().optional(),
        })
        .refine((data) => data.newPassword === data.confirmNewPassword, {
          message: t('changePassword.mismatch'),
          path: ['confirmNewPassword'],
        }),
    [t]
  )
  const form = useForm<ChangePasswordFormData>({ resolver: zodResolver(schema) })

  const { mutateAsync: changePassword, isPending } = useChangePassword({
    onSuccess: (response) => {
      if (response.success) {
        toast.success(t('changePassword.success'))
        form.reset({
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: '',
          totpCode: '',
          backupCode: '',
          emailCode: '',
        })
        setIsOpen(false)
      }
    },
    onError: (error) => {
      toast.error(error.message || t('changePassword.failed'))
    },
  })

  const onSubmit = async (data: ChangePasswordFormData) => {
    await changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      ...pickStepUpVerification(data),
    })
  }

  // Gate submit on the step-up factors: current password plus the second factor
  // for the account's mode (a TOTP or backup code, or a 6-digit emailed code).
  const mode = getStepUpMode(is2faEnabled)
  const verificationReady =
    mode === 'totp'
      ? Boolean(form.watch('totpCode')) || Boolean(form.watch('backupCode'))
      : form.watch('emailCode')?.length === 6

  return (
    <SettingsCard layout="row">
      <SettingsCardHeader title={t('password')} description={t('changePassword.description')} />
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) form.reset()
        }}
      >
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm">
            {t('changePassword.title')}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle className="flex flex-col gap-2">
            {t('changePassword.title')}
            <p className="text-foreground/60 text-sm font-normal tracking-tight">
              {t('changePassword.description')}
            </p>
          </DialogTitle>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 flex w-full flex-col gap-4"
            data-1p-ignore={false}
          >
            <div className="flex flex-col gap-1">
              <Label className="text-sm tracking-tight">{t('changePassword.newPassword')}</Label>
              <Input
                type="password"
                placeholder={t('changePassword.newPassword')}
                {...form.register('newPassword')}
                error={form.formState.errors.newPassword?.message}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-sm tracking-tight">
                {t('changePassword.confirmNewPassword')}
              </Label>
              <Input
                type="password"
                placeholder={t('changePassword.confirmNewPassword')}
                {...form.register('confirmNewPassword')}
                error={form.formState.errors.confirmNewPassword?.message}
              />
            </div>
            <StepUpVerificationFields
              form={form}
              hasPassword={hasPassword}
              totpEnabled={is2faEnabled}
              action={SensitiveAction.SENSITIVE_ACTION_CHANGE_PASSWORD}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={
                isPending ||
                form.formState.isSubmitting ||
                !form.watch('currentPassword') ||
                !verificationReady
              }
            >
              {isPending ? (
                <Loader2Icon className="size-3.5 animate-spin" />
              ) : (
                t('changePassword.submit')
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </SettingsCard>
  )
}
